// src/components/otoshop/pos.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Row, Col, Button, Card, Spinner } from 'react-bootstrap';
import Header from '../../components/Layout/Header';
import Main from '../../components/Layout/Main';
import Select from 'react-select';
import Sidebar from '../../components/Layout/Sidebar';
import ShopsContext from '../../context/shops/shopsContext';
import CustomersContext from '../../context/otogas/customers/customersContext';
import SweetAlertWrapper from "../../components/Layout/sweetAlert";
import AddPaymentMethodModal from './addPaymentMethod';
import AddOtoshopProductModal from './addShopItem';
import SetupUsageModal from './setup';
import { validate_consumption, validateShopPaymentReference } from '../../api/otogas';
import WalkInVehicleModal from '../Layout/modals/addWalkinCustomer';

// Reusable component for validation messages
const ValidationMessage = ({ message }) => {
    if (!message) return null;
    return (
        <div className={`mt-2 ${message.type === 'success' ? 'text-success' : 'text-danger'}`}>
            {message.text}
        </div>
    );
};

const PointOfSale = () => {
    const shopsContext = useContext(ShopsContext);
    const customersContext = useContext(CustomersContext);

    const {
        listProducts,
        products,
        listPaymentMethods,
        paymentMethods,
        makeSale,
        notification,
        addWalkInVehicle,
        clear_notification
    } = shopsContext;

    const { fetchVehicles, vehicles } = customersContext;

    // State Variables
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [vehicleDetails, setVehicleDetails] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [isValidated, setIsValidated] = useState(null);
    const [vehicleOptions, setVehicleOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState({ text: "", type: "" });
    const [showAlert, setShowAlert] = useState(false);
    const [paymentReference, setPaymentReference] = useState('');
    const [referenceValid, setReferenceValid] = useState(null);
    const [validationMessages, setValidationMessages] = useState({});
    const [isFullDiscount, setIsFullDiscount] = useState(false);
    const [checkedValidation, setCheckedValidation] = useState(false);

    // Modals (you were missing these - hence ESLint no-undef)
    const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] = useState(false);
    const [showAddShopProductModal, setShowAddShopProductModal] = useState(false);
    const [showSetupUsageModal, setShowSetupUsageModal] = useState(false);

    // Walk-in Modal
    const [showWalkInModal, setShowWalkInModal] = useState(false);
    const [typedRegNo, setTypedRegNo] = useState("");
    const [vehicleSearchTimer, setVehicleSearchTimer] = useState(null);
    const [isVehicleSearchLoading, setIsVehicleSearchLoading] = useState(false);
    const [hasVehicleSearchFired, setHasVehicleSearchFired] = useState(false);

    // Auto-show logic:
    // - Only schedule if search has > 6 characters
    // - Only open if NO results AND user stopped typing for 10s AND not loading
    const inactivityTimerRef = useRef(null);
    const lastTypedRef = useRef("");

    // Derived State
    const totalPrice = isFullDiscount ? 0 : (selectedProduct ? selectedProduct.price * quantity : 0);

    // Effects
    useEffect(() => {
        listProducts();
        listPaymentMethods();
    }, []);

    useEffect(() => {
        if (notification) {
            setAlertMessage({
                text: notification.text,
                type: notification.type,
            });
            setShowAlert(true);
        }
    }, [notification]);

    useEffect(() => {
        if (vehicles) {
            setVehicleOptions(
                vehicles.map(vehicle => ({
                    value: vehicle.vehicleCode,
                    label: vehicle.vehicleRegistrationNumber,
                }))
            );
        }
    }, [vehicles]);

    useEffect(() => {
        if (selectedProduct && vehicleDetails) {
            handleValidate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProduct, vehicleDetails]);

    // ------- helpers -------
    const clearInactivityTimer = () => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }
    };

    const scheduleAutoWalkinPrompt = (regNo) => {
        clearInactivityTimer();

        const clean = (regNo || "").trim();
        // USER REQUIREMENT: only show if search has more than 6 chars
        if (clean.length <= 6) return;

        inactivityTimerRef.current = setTimeout(() => {
            // Only open if:
            // - not loading
            // - we actually fired a search
            // - current typed reg still matches what we scheduled for
            // - no results
            // - modal not already open
            const current = (typedRegNo || "").trim().toUpperCase();
            const scheduled = clean.toUpperCase();

            if (
                !isVehicleSearchLoading &&
                hasVehicleSearchFired &&
                current === scheduled &&
                Array.isArray(vehicles) &&
                vehicles.length === 0 &&
                !showWalkInModal
            ) {
                setShowWalkInModal(true);
            }
        }, 10000);
    };

    const debouncedFetchVehicles = (regNo) => {
        const input = regNo || "";
        setTypedRegNo(input);
        lastTypedRef.current = input;

        // Schedule prompt only when length > 6 (as requested)
        scheduleAutoWalkinPrompt(input);

        // Debounce fetch
        if (vehicleSearchTimer) clearTimeout(vehicleSearchTimer);

        const t = setTimeout(async () => {
            const clean = (lastTypedRef.current || "").trim();

            // We can still fetch earlier for better UX, but modal is gated by > 6 chars.
            // If you want to ALSO gate fetch to >6 chars, change this to: if (clean.length <= 6) return;
            if (clean.length < 3) return;

            setHasVehicleSearchFired(true);
            setIsVehicleSearchLoading(true);
            try {
                await fetchVehicles(1, 10, "", clean, "", "", true);
            } finally {
                setIsVehicleSearchLoading(false);
            }
        }, 500);

        setVehicleSearchTimer(t);
    };

    // Clean up timers on unmount
    useEffect(() => {
        return () => {
            if (vehicleSearchTimer) clearTimeout(vehicleSearchTimer);
            clearInactivityTimer();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleSearchTimer]);

    // Handlers
    const handleValidate = async () => {
        if (!selectedProduct || !vehicleDetails) {
            setValidationMessages({
                vehicle: { text: 'Please select both a product and a vehicle.', type: 'danger' }
            });
            setIsValidated(false);
            return;
        }

        setValidationMessages({});
        setIsLoading(true);

        try {
            const response = await validate_consumption(vehicleDetails.value, selectedProduct.value);
            if (response.responseCode === 1) {
                setIsValidated(true);
                setValidationMessages({
                    vehicle: { text: 'Vehicle and product combination is valid.', type: 'success' }
                });
            } else {
                setIsValidated(false);
                setValidationMessages({
                    vehicle: { text: response.responseMessage || 'Selected vehicle is not allowed for this product.', type: 'danger' }
                });
            }
        } catch {
            setIsValidated(false);
            setValidationMessages({
                vehicle: { text: 'Validation failed. Please try again.', type: 'danger' }
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleReferenceBlur = async () => {
        if (!paymentReference) {
            setValidationMessages({
                paymentReference: { text: 'Please enter a payment reference.', type: 'danger' }
            });
            setReferenceValid(false);
            return;
        }

        setIsLoading(true);

        try {
            const data = await validateShopPaymentReference(paymentReference);
            if (data.responseCode === 1 && data.responseObject?.usageBalance >= totalPrice) {
                setReferenceValid(true);
                setCheckedValidation(true);
                setValidationMessages({
                    paymentReference: { text: 'Payment reference is valid.', type: 'success' }
                });
            } else if (data.responseCode === 2) {
                setReferenceValid(false);
                setCheckedValidation(true);
                setValidationMessages({
                    paymentReference: { text: `Invalid payment reference.`, type: 'danger' }
                });
            } else {
                setReferenceValid(false);
                setCheckedValidation(true);
                setValidationMessages({
                    paymentReference: {
                        text: `Insufficient balance. Available: KSH ${data.responseObject?.usageBalance}, Required: KSH ${totalPrice}.`,
                        type: 'danger'
                    }
                });
            }
        } catch {
            setReferenceValid(false);
            setValidationMessages({
                paymentReference: { text: 'Validation error. Try again.', type: 'danger' }
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSell = async () => {
        if (!paymentMethod) {
            setAlertMessage({ text: 'Please select a payment method.', type: 'danger' });
            setShowAlert(true);
            return;
        }

        if (!vehicleDetails?.value) {
            setAlertMessage({ text: 'Please select a vehicle.', type: 'danger' });
            setShowAlert(true);
            return;
        }

        if (!selectedProduct?.value) {
            setAlertMessage({ text: 'Please select a product.', type: 'danger' });
            setShowAlert(true);
            return;
        }

        const payload = {
            totalAmount: totalPrice,
            paymentMethod,
            vehicleCode: vehicleDetails.value,
            mpesaReference: paymentReference,
            sales: [
                {
                    itemCode: selectedProduct.value,
                    quantity,
                    price: selectedProduct.price,
                },
            ],
        };

        setIsLoading(true);

        try {
            await makeSale(payload);

            // Reset form
            setSelectedProduct(null);
            setQuantity(1);
            setVehicleDetails(null);
            setPaymentMethod('');
            setIsValidated(null);
            setCheckedValidation(false);
            setIsFullDiscount(false);
            setPaymentReference('');
            setReferenceValid(null);
            setValidationMessages({});
            setHasVehicleSearchFired(false);
            setTypedRegNo("");
            clearInactivityTimer();
        } catch {
            setAlertMessage({ text: 'Sale failed. Please try again.', type: 'danger' });
            setShowAlert(true);
        } finally {
            setIsLoading(false);
        }
    };

    const registerWalkInAndSelect = async (payload) => {
        // NOTE: ShopsState.addWalkInVehicle MUST return res for this to work properly.
        const res = await addWalkInVehicle(payload);

        if (res?.responseCode === 1) {
            setShowWalkInModal(false);

            // refresh vehicle list with the new reg and attempt to auto-select
            const reg = payload.vehicleRegistrationNumber;
            setTypedRegNo(reg);

            setIsVehicleSearchLoading(true);
            try {
                await fetchVehicles(1, 10, "", reg, "", "", true);
            } finally {
                setIsVehicleSearchLoading(false);
            }

            setTimeout(() => {
                const clean = (reg || "").trim().toUpperCase();
                const found = (vehicles || []).find(
                    (v) => (v.vehicleRegistrationNumber || "").trim().toUpperCase() === clean
                );

                if (found) {
                    setVehicleDetails({
                        value: found.vehicleCode,
                        label: found.vehicleRegistrationNumber,
                    });
                }
            }, 500);
        }

        return res;
    };

    return (
        <div>
            <Header />
            <Sidebar page="pos" />
            <Main>
                <SweetAlertWrapper
                    show={showAlert}
                    title="Notification"
                    message={alertMessage.text}
                    onConfirm={() => {
                        setShowAlert(false);
                        clear_notification();
                    }}
                    confirmBtnText="OK"
                    type={alertMessage.type || "info"}
                />

                <Card className="p-3 mb-4">
                    <Row className="align-items-center">
                        <Col>
                            <h3 className="mb-0">Otoshop</h3>
                        </Col>
                        <Col className="text-end">
                            <Button
                                variant="primary"
                                className="me-2"
                                onClick={() => setShowAddShopProductModal(true)}
                            >
                                Add Product
                            </Button>
                            <Button
                                variant="secondary"
                                className="me-2"
                                onClick={() => setShowAddPaymentMethodModal(true)}
                            >
                                Add Payment Method
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => setShowSetupUsageModal(true)}
                            >
                                Setup Usage
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Row>
                    <Col md={8}>
                        <Card className="mb-4">
                            <Card.Header as="h5">Sales Form</Card.Header>
                            <Card.Body>
                                <h6>Select Product</h6>
                                <Select
                                    options={products.map(product => ({
                                        value: product.itemCode,
                                        label: product.itemName,
                                        price: product.price,
                                        hasDiscount: product.hasDiscount,
                                    }))}
                                    onChange={option => {
                                        setSelectedProduct(option);
                                        setIsValidated(false);
                                        setVehicleDetails(null);
                                        setValidationMessages({});
                                        clearInactivityTimer();
                                    }}
                                    placeholder="Select a product"
                                />

                                {selectedProduct && (
                                    <>
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <h6 className="mb-0">Select Vehicle</h6>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => setShowWalkInModal(true)}
                                            >
                                                Register Walk-in Vehicle
                                            </Button>
                                        </div>

                                        <Select
                                            options={vehicleOptions}
                                            onInputChange={debouncedFetchVehicles}
                                            onChange={(option) => {
                                                setVehicleDetails(option);
                                                setValidationMessages({});
                                                clearInactivityTimer(); // user acted, stop auto prompt
                                                if (selectedProduct && option) handleValidate();
                                            }}
                                            placeholder="Search and select a vehicle"
                                            noOptionsMessage={() => {
                                                const clean = (typedRegNo || "").trim();
                                                if (clean.length <= 2) return "Type at least 3 characters to search";
                                                if (isVehicleSearchLoading) return "Searching vehicles...";
                                                return "No results found";
                                            }}
                                        />

                                        <div className="mt-2 d-flex align-items-center gap-2">
                                            {isVehicleSearchLoading && (
                                                <>
                                                    <Spinner animation="border" size="sm" />
                                                    <span className="text-muted">Searching vehicles...</span>
                                                </>
                                            )}

                                            {!isVehicleSearchLoading &&
                                                hasVehicleSearchFired &&
                                                (typedRegNo || "").trim().length > 6 &&
                                                Array.isArray(vehicles) &&
                                                vehicles.length === 0 && (
                                                    <span className="text-muted">
                                                        No results for “{typedRegNo}”. If you don’t type anything for 10 seconds, we’ll prompt to register as Walk-in.
                                                    </span>
                                                )}
                                        </div>

                                        <ValidationMessage message={validationMessages.vehicle} />
                                    </>
                                )}

                                {vehicleDetails && selectedProduct ? (
                                    <>
                                        <h6 className="mt-3">Select Payment Type</h6>
                                        <Select
                                            options={paymentMethods
                                                .filter(method => {
                                                    if (selectedProduct?.hasDiscount) {
                                                        if (isValidated) {
                                                            return method.paymentMethod === "Full Discount";
                                                        } else {
                                                            return method.paymentMethod !== "Full Discount";
                                                        }
                                                    } else {
                                                        return method.paymentMethod !== "Full Discount";
                                                    }
                                                })
                                                .map(method => ({
                                                    value: method.paymentMethod,
                                                    label: method.paymentMethod,
                                                }))}
                                            onChange={option => {
                                                setPaymentMethod(option?.value || '');
                                                setIsFullDiscount(option?.value === "Full Discount");
                                                setPaymentReference('');
                                                setReferenceValid(null);
                                                setValidationMessages({});
                                            }}
                                            placeholder="Select Payment Method"
                                        />
                                    </>
                                ) : null}

                                {(["Mpesa", "Wallet"].includes(paymentMethod)) && (
                                    <>
                                        <h6 className="mt-3">Payment Reference</h6>
                                        <div className="d-flex">
                                            <input
                                                type="text"
                                                value={paymentReference}
                                                onChange={(e) => {
                                                    setPaymentReference(e.target.value);
                                                    setReferenceValid(null);
                                                    setValidationMessages({});
                                                }}
                                                className="form-control"
                                                placeholder="Enter Payment Reference"
                                            />
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="ms-2"
                                                onClick={handleReferenceBlur}
                                                disabled={isLoading || !paymentReference}
                                            >
                                                {isLoading ? <Spinner animation="border" size="sm" /> : 'Validate'}
                                            </Button>
                                        </div>
                                        <ValidationMessage message={validationMessages.paymentReference} />
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="mb-4">
                            <Card.Header as="h5">Invoice Summary</Card.Header>
                            <Card.Body>
                                <p>
                                    <strong>Vehicle:</strong> {vehicleDetails?.label || 'N/A'}
                                </p>
                                <p>
                                    <strong>Payment Method:</strong> {paymentMethod || 'N/A'}
                                </p>
                                <hr />
                                <p>
                                    <strong>Product:</strong> {selectedProduct?.label || 'N/A'}
                                </p>
                                <p>
                                    <strong>Quantity:</strong> {quantity}
                                </p>
                                <p>
                                    <strong>Total Price:</strong> <span className="text-success">KSH {totalPrice}</span>
                                </p>
                                <Button
                                    variant="success"
                                    className="mt-3 w-100"
                                    onClick={handleSell}
                                    disabled={
                                        isLoading ||
                                        isVehicleSearchLoading ||
                                        !paymentMethod ||
                                        !vehicleDetails ||
                                        !selectedProduct ||
                                        (["Mpesa", "Wallet"].includes(paymentMethod) && !referenceValid)
                                    }
                                >
                                    {isLoading ? <Spinner animation="border" size="sm" /> : 'Sell'}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <AddOtoshopProductModal
                    show={showAddShopProductModal}
                    handleClose={() => setShowAddShopProductModal(false)}
                />
                <AddPaymentMethodModal
                    show={showAddPaymentMethodModal}
                    handleClose={() => setShowAddPaymentMethodModal(false)}
                />
                <SetupUsageModal
                    show={showSetupUsageModal}
                    handleClose={() => setShowSetupUsageModal(false)}
                />

                <WalkInVehicleModal
                    show={showWalkInModal}
                    handleClose={() => {
                        setShowWalkInModal(false);
                        clearInactivityTimer();
                    }}
                    defaultReg={typedRegNo}
                    onSubmit={registerWalkInAndSelect}
                />
            </Main>
        </div>
    );
};

export default PointOfSale;
